import React, { Fragment } from 'react';
import { G, Path } from '@react-pdf/renderer';
import { ceil, clamp, mapValues, round } from 'lodash';

/**
 * Interface for checked ceiling tile
 */
interface CheckedTile {
  x: number;
  y: number;
  mask: number;
}

/**
 * Interface for ceiling tile dimensions
 */
interface TileDimensions {
  x: number;
  y: number;
  [key: string]: number;
}

/**
 * Interface for ceiling tile quantity
 */
interface TileQuantity {
  x: number;
  y: number;
}

/**
 * Interface for tile transform
 */
interface TileTransform {
  x: number;
  y: number;
}

/**
 * Interface for area dimensions
 */
interface AreaDimensions {
  length: number;
  width: number;
}

/**
 * Interface for area ceiling tiles
 */
interface AreaCeilingTiles {
  checked: CheckedTile[];
  qty: TileQuantity;
  tile: string;
  transform: TileTransform;
}

/**
 * Interface for area data
 */
interface Area {
  ceilingTiles: AreaCeilingTiles;
  dimensions: AreaDimensions;
  [key: string]: any;
}

/**
 * Interface for areas object
 */
interface Areas {
  [key: string]: Area;
}

/**
 * Interface for content
 */
interface Content {
  areas: Areas;
  [key: string]: any;
}

/**
 * Interface for ceiling tiles config
 */
interface CeilingTilesConfig {
  [key: string]: TileDimensions;
}

/**
 * Interface for constants
 */
interface Constants {
  ceilingTiles: CeilingTilesConfig;
  [key: string]: any;
}

/**
 * Interface for bounding box
 */
interface BoundingBox {
  width: number;
  height: number;
}

/**
 * Interface for path data
 */
interface PathData {
  d: string;
}

/**
 * Interface for component props
 */
interface MuiCeilingProps {
  areaId: string;
  scale: number;
  bbox: BoundingBox;
  content: Content;
  constants: Constants;
}

/**
 * MUI Ceiling component that replaces the styled-components Ceiling component
 * 
 * This component provides a Material UI equivalent of the original Ceiling component
 * with similar props and functionality. It's used for PDF generation.
 * 
 * @param {MuiCeilingProps} props - Component props
 * @returns {JSX.Element} MUI Ceiling component
 */
const MuiCeiling: React.FC<MuiCeilingProps> = (props) => {
  const { areaId, scale, bbox, content: { areas }, constants: { ceilingTiles } } = props;
  const { ceilingTiles: { checked, qty, tile, transform }, dimensions: { length, width } } = areas[areaId];
  const tileSize = mapValues(ceilingTiles[tile], (v) => v * scale);

  // Calculate the maximum number of tiles that fit in the room 
  // Add 1 to ensure full coverage when grid is centered
  const maxX = ceil(length * scale / tileSize.x) + 1;
  const maxY = ceil(width * scale / tileSize.y) + 1;

  // Check that qty is within max
  let { x = 0, y = 0 } = qty;
  x = x ? clamp(x, Math.min(1, maxX), maxX) : qty.x > 0 ? maxX : 0;
  y = y ? clamp(y, Math.min(1, maxY), maxY) : qty.y > 0 ? maxY : 0;

  // Set draw quantities - add extra tiles for movement buffer
  // This ensures the grid can be moved without gaps appearing
  const drawX = x + 2;
  const drawY = y + 2;

  // get the center of the tile arrray
  const cx = round((drawX * tileSize.x) * 2) / 4;
  const cy = round((drawY * tileSize.y) * 2) / 4;

  // Set the transformation
  const tx = (bbox.width / 2) - cx + transform.x * scale;
  const ty = (bbox.height / 2) - cy + transform.y * scale;

  // create the lines array
  const lines: PathData[] = [...Array(drawX + 1)].map((_, x) => {
    const x0 = round(tileSize.x * x * 2) / 2;
    const y1 = round((drawY * tileSize.y) * 2) / 2;
    return { d: `m ${x0} 0 v${y1}` }
  });
  lines.push(...[...Array(drawY + 1)].map((_, y) => {
    const y0 = round(tileSize.y * y * 2) / 2;
    const x1 = round((drawX * tileSize.x) * 2) / 2;
    return { d: `m 0 ${y0} h${x1}` }
  }))

  // Add the tile crosses
  const tileShape = Math.sign(tileSize.x - tileSize.y); // returns -1 for vertical, 0 for square and 1 for horizontal

  checked.forEach((ck, id) => {
    const x = 2 + ck.x * tileSize.x;
    const y = 2 + ck.y * tileSize.y;
    const side = round((Math.min(tileSize.x, tileSize.y) - 4) * 2) / 2;
    if (tileShape === 0 || ck.mask <= 0) lines.push({ d: `m ${x} ${y} l ${side} ${side} m 0 -${side} l -${side} ${side}` });
    if (tileShape !== 0 && ck.mask >= 0) lines.push({ d: `m ${x + (tileShape > 0 ? (side + 2) : 0)} ${y + (tileShape < 0 ? (side + 4) : 0)} l ${side} ${side} m 0 -${side} l -${side} ${side}` });
  });

  return (
    <Fragment>
      <G transform={`translate(${round(tx)}, ${round(ty)})`} >
        {lines.map((line, index) => <Path key={`line-${index}`} {...line} stroke="#3B82F6" strokeWidth={1} strokeOpacity={0.8} {...{'shapeRendering': 'crispEdges'} as any} />)}
      </G>
    </Fragment>
  );
};

export default MuiCeiling;
